import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useTranslation } from "react-i18next"
import { getGiftCardUsageDetails } from "store/actions/giftCardAction"
import formatDateTime from "components/Shared/formatDateTime"
import LtrText from "components/Shared/LtrText"
import isPermissionEnabled from "common/isPermissionEnabled"

const GiftCardUsageDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Gift Card Reports Details ")
  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const { loading, giftCardUsageDetails } = useSelector(state => state.GiftCard)

  const { userRole, accountType } = useSelector(state => state.Login)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      38,
      "gift_card_usage_1",
      accountType
    )

    if (!isPermissionList) {
      navigate("/dashboard")
    } else {
      getOldValues()
    }
  }, [userRole])

  const getOldValues = () => {
    dispatch(getGiftCardUsageDetails(id))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        {loading ? (
          <Card>
            <CardBody>
              <Row>
                <Col lg={12}>
                  <ShimmerThumbnail height={600} rounded />
                </Col>
              </Row>
            </CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3 d-flex align-items-center">
                    <BackButton /> {t("Gift Card Reports Details")}
                  </CardTitle>
                  <div className="table-responsive">
                    {Object.values(giftCardUsageDetails).length === 0 ? (
                      <p className="text-center">
                        {t("No gift card usage details found")}
                      </p>
                    ) : (
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <h5 className="fw-bold text-black mb-0">
                            {t("Gift Card details")}
                          </h5>
                          <tr>
                            <th scope="row">{t("Id")}</th>
                            <td>{giftCardUsageDetails._id}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Gift Card Name(English)")}</th>
                            <td>
                              {giftCardUsageDetails?.giftCardId?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Gift Card Name(عربي)")}</th>
                            <td>
                              {giftCardUsageDetails?.giftCardId?.nameAr || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Gift Card Amount")}</th>
                            <td>
                              {giftCardUsageDetails?.amount ?? "-"}{" "}
                              {giftCardUsageDetails?.currency}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Gift Card No")}</th>
                            <td>{giftCardUsageDetails?.giftCardNo ?? "-"} </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("Message")}</th>
                            <td>
                              <p className="text-wrap mb-0">
                                {giftCardUsageDetails?.message ?? "-"}
                              </p>
                            </td>
                          </tr> */}
                          <tr>
                            <th scope="row">{t("Redeemed")}</th>
                            <td>
                              {giftCardUsageDetails.redeemed ? (
                                <Badge className="bg-success">
                                  {t("Redeemed")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Not Redeem")}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("Expires At")}</th>
                            <td>
                              {formatDateTime(giftCardUsageDetails?.expiresAt)}{" "}
                            </td>
                          </tr> */}

                          <tr>
                            <th scope="row">{t("Created At")}</th>
                            <td>
                              <p className="ltr w_fit">
                                {formatDateTime(
                                  giftCardUsageDetails?.createdAt
                                )}{" "}
                              </p>
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Updated At")}</th>
                            <td>
                              <p className="ltr w_fit">
                                {formatDateTime(
                                  giftCardUsageDetails?.updatedAt
                                )}{" "}
                              </p>
                            </td>
                          </tr>

                          <h5 className="fw-bold text-black mb-0 mt-3">
                            {t("Sender Information")}
                          </h5>

                          {/* <tr>
                            <th scope="row">{t("Sender Name")}</th>
                            <td>
                              {giftCardUsageDetails?.senderId?.name || "-"}{" "}
                            </td>
                          </tr> */}

                          <tr>
                            <th scope="row">{t("Sender Phone Number")}</th>
                            <td>
                              {giftCardUsageDetails?.senderId?.phoneNumber ? (
                                <LtrText>
                                  {giftCardUsageDetails.senderId.phoneNumber}
                                </LtrText>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>

                          <h5 className="fw-bold text-black mb-0 mt-3">
                            {t("Receiver Information")}
                          </h5>

                          {/* <tr>
                            <th scope="row">{t("Receiver Name")}</th>
                            <td>
                              <p className="ltr w_fit">
                                {giftCardUsageDetails?.receiverName || "-"}{" "}
                              </p>
                            </td>
                          </tr> */}
                          <tr>
                            <th scope="row">{t("Receiver Phone Number")}</th>
                            <td>
                              <p className="ltr w_fit">
                                {giftCardUsageDetails?.receiverNumber || "-"}{" "}
                              </p>
                            </td>
                          </tr>

                          {giftCardUsageDetails.redeemed && (
                            <tr>
                              <th scope="row">{t("Redeemed At")}</th>
                              <td>
                                <p className="ltr w_fit">
                                  {formatDateTime(
                                    giftCardUsageDetails?.redeemedAt
                                  )}{" "}
                                </p>
                              </td>
                            </tr>
                          )}

                          <h5 className="fw-bold text-black mb-0 mt-3">
                            {t("Transaction Details")}
                          </h5>

                          <tr>
                            <th scope="row">{t("Payment Method")}</th>
                            <td>
                              {giftCardUsageDetails?.transactionDetails
                                ?.paymentMethod || "-"}{" "}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Payment Status")}</th>
                            <td>
                              {giftCardUsageDetails.paymentStatus === 1 ? (
                                <Badge className="bg-success">
                                  {t("Paid")}
                                </Badge>
                              ) : giftCardUsageDetails.paymentStatus === 0 ? (
                                <Badge className="bg-warning">
                                  {t("Pending")}
                                </Badge>
                              ) : (
                                "-"
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Transaction Id")}</th>
                            <td>
                              {giftCardUsageDetails?.transactionDetails
                                ?.transactionId || "-"}{" "}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Payment Gateway")}</th>
                            <td>
                              {giftCardUsageDetails?.transactionDetails
                                ?.gateway || "-"}{" "}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Gateway Status")}</th>
                            <td>
                              {giftCardUsageDetails?.transactionDetails
                                ?.gatewayStatus || "-"}{" "}
                            </td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("Unique Code")}</th>
                            <td>{giftCardUsageDetails?.uniqueCode || "-"} </td>
                          </tr> */}
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  )
}

export default GiftCardUsageDetails
